import React, { useState, useEffect } from 'react';
import InstallCard from './account/InstallCard';
import DeleteAccountCard from './account/DeleteAccountCard';
import { SetupWizardModal } from './setup/SetupWizardModal';
import { getUsers, deleteUser, createInvite, getInvites, deleteInvite, generateResetToken } from '../api';
import {
  getBudgetMode,
  getVarBudget,
  getAllBills,
  moIncome,
  mk,
  fc,
  getAllCats,
  nextDueDate,
  getSmartCatBudgets,
  getLevelInfo,
} from '../utils';

const COLORS = ['#ff6b6b', '#4d96ff', '#ffd93d', '#6bcb77', '#ff6bdf', '#7C6FF7', '#f0a500'];

const WIDGET_LIST = [
  { id: 'paycheckPlan', label: 'This Paycheck (Autopilot)', desc: 'Where every dollar of your next paycheck should go' },
  { id: 'budget', label: 'Budget Card', desc: 'Discretionary budget & forecast' },
  { id: 'streaks', label: 'Streak Badges', desc: 'Budget & savings streak pills' },
  { id: 'recap', label: 'Month-End Recap', desc: "Last month's performance summary" },
  { id: 'billsDue', label: 'Bills Due Soon', desc: 'Bills coming up this week' },
  { id: 'billCalendar', label: 'Bill Calendar', desc: 'Monthly calendar view of due dates' },
  { id: 'metrics', label: 'Top Metrics', desc: 'Debt / financial position / debt-free date' },
  { id: 'health', label: 'Health Score', desc: 'Financial health score (0–100)' },
  { id: 'savingsChart', label: 'Savings Chart', desc: 'Savings balance over time' },
  { id: 'netWorth', label: 'Net Worth Chart', desc: 'Savings minus debt over time' },
  { id: 'spendingTrends', label: 'Spending Trends', desc: '6-month variable spending chart' },
  { id: 'debtSnapshot', label: 'Debt Snapshot', desc: 'Per-account progress cards' },
  { id: 'ytd', label: 'Year to Date', desc: 'Annual income/spending summary' },
  { id: 'milestones', label: 'Milestones', desc: 'Achievement badges' },
  { id: 'insights', label: 'Spending Insights', desc: 'Per-category averages vs current month' },
  { id: 'tips', label: 'Financial Tips', desc: 'Contextual money tips based on your data' },
  { id: 'cashFlow', label: 'Cash Flow', desc: 'Monthly income vs planned spending breakdown' },
  { id: 'cashFlowForecast', label: 'Cash Flow Forecast', desc: 'Upcoming paychecks & bills timeline with a projected running balance' },
  { id: 'payoffCompare', label: 'Payoff Comparison', desc: 'Snowball vs Avalanche side-by-side' },
  { id: 'recurringCharges', label: 'Recurring Charges', desc: 'Auto-detected recurring expenses' },
  { id: 'dailyAllowance', label: "Today's Allowance", desc: 'How much you can spend today' },
];

const SUB_COLORS = ['#ff6b6b', '#4d96ff', '#ffd93d', '#6bcb77', '#ff6bdf', '#7C6FF7', '#f0a500'];

export default function Settings({
  state,
  updateState,
  showToast,
  isAdmin,
  onPasswordChange,
  theme,
  setTheme,
  only, // render just one section ('budget' | 'bills') without the settings tab bar
}) {
  const [tab, setTab] = useState(only || 'appear');
  const [wizardOpen, setWizardOpen] = useState(false);
  const currentLevel = getLevelInfo(state).level;
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [copied, setCopied] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLinks, setResetLinks] = useState({}); // userId → link

  // Password change
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confPw, setConfPw] = useState('');
  const [pwErr, setPwErr] = useState('');

  const slider = state.budgetSlider ?? 50;
  const mode = getBudgetMode(slider);
  const now = new Date();
  const k = mk(now.getFullYear(), now.getMonth());
  const inc =
    state.incomeMode === 'monthly' ? state.monthlyIncome || 0 : moIncome(state.incomeLog || [], k);
  const allBills = getAllBills(
    state.cards || [],
    state.fixedBills || [],
    state.subscriptions || []
  );
  const { budget: varBudget } = getVarBudget(inc, allBills, state.savContrib || 0, slider);

  useEffect(() => {
    if (tab === 'users' && isAdmin) {
      loadUsers();
      loadInvites();
    }
    // isAdmin is stable after login; loadUsers/loadInvites are defined below and stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);

  const loadUsers = async () => {
    const r = await getUsers();
    if (r.users) setUsers(r.users);
  };
  const loadInvites = async () => {
    const r = await getInvites();
    if (r.invites) setInvites(r.invites);
  };

  const handleResetPassword = async (id) => {
    const r = await generateResetToken(id);
    if (r.link) {
      setResetLinks((prev) => ({ ...prev, [id]: r.link }));
      navigator.clipboard.writeText(r.link).catch(() => {});
      showToast('Reset link copied to clipboard — valid 1 hour');
    } else {
      showToast('Failed: ' + (r.error || 'unknown error'));
    }
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm('Remove access for ' + name + '? This cannot be undone.')) return;
    await deleteUser(id);
    showToast('User removed');
    loadUsers();
  };

  const handleCreateInvite = async () => {
    setLoading(true);
    const r = await createInvite();
    setLoading(false);
    if (r.link) {
      const token = r.link.split('token=')[1];
      const link = window.location.origin + '/register?token=' + token;
      setInvites((inv) => [
        { id: Date.now(), token, expires_at: r.expires, used_at: null, used_by: null, link },
        ...inv,
      ]);
      showToast('Invite link created!');
    } else {
      showToast('Failed to generate invite — ' + (r.error || 'unknown error'));
    }
  };

  const copyLink = (link, id) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const handleDeleteInvite = async (id) => {
    await deleteInvite(id);
    setInvites((inv) => inv.filter((i) => i.id !== id));
    showToast('Invite removed');
  };

  const handlePasswordChange = async () => {
    setPwErr('');
    if (!curPw || !newPw || !confPw) {
      setPwErr('All fields required');
      return;
    }
    if (newPw.length < 8) {
      setPwErr('New password must be at least 8 characters');
      return;
    }
    if (newPw !== confPw) {
      setPwErr('New passwords do not match');
      return;
    }
    const res = await onPasswordChange(curPw, newPw);
    if (res.ok) {
      showToast('Password changed!');
      setCurPw('');
      setNewPw('');
      setConfPw('');
    } else {
      setPwErr(res.error || 'Failed to change password');
    }
  };

  // Bills
  const [billName, setBillName] = useState('');
  const [billAmt, setBillAmt] = useState('');
  const [billDue, setBillDue] = useState('');
  const [billCol, setBillCol] = useState(COLORS[0]);
  const [billErr, setBillErr] = useState('');
  const [editingBill, setEditingBill] = useState(null); // { id, name, amt, due, col }

  const fixedBills = state.fixedBills || [];
  const clampDue = (v) => Math.min(31, Math.max(1, parseInt(v) || 1));

  const addBill = () => {
    if (!billName || !billAmt) {
      setBillErr('Fill in bill name and amount');
      return;
    }
    if (parseFloat(billAmt) <= 0) {
      setBillErr('Amount must be greater than $0');
      return;
    }
    const id = 'bill_' + Date.now();
    updateState({
      fixedBills: [
        ...fixedBills,
        { id, name: billName, amt: parseFloat(billAmt), due: clampDue(billDue), col: billCol },
      ],
    });
    setBillName('');
    setBillAmt('');
    setBillDue('');
    setBillErr('');
    showToast('Bill added');
  };

  const removeBill = (id) => {
    updateState({ fixedBills: fixedBills.filter((b) => b.id !== id) });
    showToast('Bill removed');
  };

  const saveEditBill = () => {
    if (!editingBill.name || !editingBill.amt) return;
    updateState({
      fixedBills: fixedBills.map((b) =>
        b.id === editingBill.id
          ? {
              ...b,
              name: editingBill.name,
              amt: parseFloat(editingBill.amt),
              due: clampDue(editingBill.due),
              col: editingBill.col,
            }
          : b
      ),
    });
    setEditingBill(null);
    showToast('Bill updated');
  };


  const customCats = state.customCats || [];
  const allCats = getAllCats(customCats, state.disabledCats);

  const exportData = () => {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importBackup = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) { showToast('File too large (max 2 MB)'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target.result);
          if (typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Invalid format');
          const VALID_KEYS = ['fixedBills','subscriptions','cards','varSpend','incomeLog','paidBills','savDeposits','monthlyIncome','budgetSlider','catBudgets','savContrib','incomeSources','bankBalance'];
          const hasKnownKey = VALID_KEYS.some(k => k in parsed);
          if (!hasKnownKey) throw new Error('Not a valid backup file');
          if (!window.confirm('Replace all your current data with this backup? This cannot be undone.')) return;
          updateState(parsed);
          showToast('✅ Backup restored successfully!');
        } catch {
          showToast('❌ Invalid backup file — could not restore');
        }
      };
      reader.onerror = () => showToast('❌ Could not read file');
      reader.readAsText(file);
    };
    input.click();
  };

  const exportCSV = () => {
    const cats = getAllCats(state.customCats || [], state.disabledCats || []);
    const catMap = Object.fromEntries(cats.map((c) => [c.id, c.name]));
    const q = (v) => '"' + String(v ?? '').replace(/"/g, '""') + '"';
    const dataRows = [];
    const varSpend = state.varSpend || {};
    Object.entries(varSpend).forEach(([key, month]) => {
      const [yr, mo] = key.split('-').map(Number);
      Object.entries(month).forEach(([catId, txns]) => {
        (txns || []).forEach((tx) => {
          // ISO date avoids comma-in-date issues from toLocaleDateString()
          const date = tx.ts
            ? new Date(tx.ts).toISOString().split('T')[0]
            : `${yr}-${String(mo + 1).padStart(2, '0')}-01`;
          dataRows.push({ date, cat: catMap[catId] || catId, place: tx.place || '', amt: tx.amt });
        });
      });
    });
    dataRows.sort((a, b) => a.date.localeCompare(b.date));
    const header = [q('Date'), q('Category'), q('Place / Description'), q('Amount')].join(',');
    const body = dataRows.map((r) => [q(r.date), q(r.cat), q(r.place), r.amt].join(',')).join('\n');
    const csv = header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Subscriptions
  const [subName, setSubName] = useState('');
  const [subAmt, setSubAmt] = useState('');
  const [subCycle, setSubCycle] = useState('monthly');
  const [subDue, setSubDue] = useState('');
  const [subCol, setSubCol] = useState(SUB_COLORS[0]);
  const subscriptions = state.subscriptions || [];

  const addSub = () => {
    if (!subName || !subAmt) {
      showToast('Enter name and amount');
      return;
    }
    if (parseFloat(subAmt) <= 0) {
      showToast('Amount must be greater than $0');
      return;
    }
    if (!subDue || parseInt(subDue) < 1 || parseInt(subDue) > 31) {
      showToast('Enter the day of month it charges (1–31)');
      return;
    }
    const id = 'sub_' + Date.now();
    updateState({
      subscriptions: [
        ...subscriptions,
        {
          id,
          name: subName,
          amt: parseFloat(subAmt),
          cycle: subCycle,
          due: parseInt(subDue) || 1,
          col: subCol,
        },
      ],
    });
    setSubName('');
    setSubAmt('');
    setSubDue('');
    showToast('Subscription added');
  };
  const removeSub = (id) => {
    updateState({ subscriptions: subscriptions.filter((s) => s.id !== id) });
    showToast('Removed');
  };

  // Dashboard widget toggles
  const dashWidgets = {
    budget: true,
    streaks: true,
    nudge: true,
    billsDue: true,
    metrics: true,
    monthly: true,
    savingsChart: true,
    spendingTrends: true,
    debtSnapshot: true,
    ytd: true,
    health: true,
    milestones: true,
    billCalendar: true,
    netWorth: true,
    recap: true,
    affordCalc: true,
    insights: true,
    tips: true,
    ...(state.dashWidgets || {}),
  };
  const setWidget = (id, val) => updateState({ dashWidgets: { ...dashWidgets, [id]: val } });

  const settingsTabs = [
    { id: 'appear',   label: '🎨 Appearance' },
    { id: 'account',  label: '👤 Account' },
    { id: 'feedback', label: '🐛 Feedback' },
    ...(isAdmin ? [{ id: 'users', label: '👥 Users' }, { id: 'reports', label: '🚩 Reports' }] : []),
  ];

  return (
    <div>
      {!only && <div className="card cp" style={{ marginBottom: 12 }}>
        <div className="st">⚙️ Settings</div>
        <div className="stabs">
          {settingsTabs.map((t) => (
            <button
              key={t.id}
              className={'stab' + (tab === t.id ? ' on' : '')}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>}

      {/* Budget Mode */}
      {tab === 'budget' && (
        <div className="card cp">
          <div className="st">💸 Discretionary Budget</div>
          <p style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 16 }}>
            Set how much of your free money (after bills &amp; savings) goes toward eating out,
            entertainment, and other variable spending.
          </p>
          <div className="bm-preview" style={{ borderColor: mode.color, marginBottom: 20 }}>
            <div className="bm-emoji">{mode.emoji}</div>
            <div>
              <div className="bm-name" style={{ color: mode.color }}>
                {mode.name}
              </div>
              <div className="bm-desc">{mode.desc}</div>
            </div>
          </div>
          {inc > 0 && slider > 0 && (
            <div
              style={{
                background: 'var(--surf2)',
                borderRadius: 9,
                padding: '10px 14px',
                border: '1px solid ' + mode.color,
                marginBottom: 16,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 2 }}>
                Your budget this month
              </div>
              <div
                style={{
                  fontFamily: 'Syne,sans-serif',
                  fontSize: 28,
                  fontWeight: 800,
                  color: mode.color,
                }}
              >
                {fc(varBudget)}
              </div>
            </div>
          )}
          {inc === 0 && (
            <div
              style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 16, textAlign: 'center' }}
            >
              Log paychecks to see your dollar budget
            </div>
          )}
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 11,
                color: 'var(--mut)',
                marginBottom: 8,
              }}
            >
              <span>💰 Save all</span>
              <span>⚖️ Balanced</span>
              <span>🎉 Spend freely</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={slider}
              onChange={(e) => updateState({ budgetSlider: +e.target.value })}
              style={{ width: '100%' }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 10,
                color: 'var(--mut)',
                marginTop: 4,
              }}
            >
              <span>0%</span>
              <span style={{ color: mode.color, fontWeight: 600 }}>{slider}% of free money</span>
              <span>100%</span>
            </div>
          </div>
          <div
            style={{
              background: 'var(--surf2)',
              borderRadius: 10,
              padding: '12px 14px',
              border: '1px solid var(--bdr)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                marginBottom: 8,
                color: 'var(--mut)',
                textTransform: 'uppercase',
                letterSpacing: '.4px',
              }}
            >
              How it works
            </div>
            <div style={{ fontSize: 11, color: 'var(--mut)', lineHeight: 1.7 }}>
              Free money = income − fixed bills − savings contribution.
              <br />
              Your discretionary budget = {slider}% of that amount.
              <br />
              The rest is yours to put toward debt payoff or extra savings.
            </div>
          </div>

          {/* Budget carryover toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
              padding: '12px 14px',
              background: 'var(--surf2)',
              borderRadius: 10,
              border: '1px solid var(--bdr)',
              marginTop: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 3 }}>
                Roll over unused budget
              </div>
              <div style={{ fontSize: 11, color: 'var(--mut)', lineHeight: 1.6 }}>
                When on, any unspent discretionary budget carries into next month.
                When off, the budget resets on the 1st.
              </div>
            </div>
            <button
              onClick={() => updateState({ budgetCarryover: !state.budgetCarryover })}
              style={{
                flexShrink: 0,
                width: 44,
                height: 24,
                borderRadius: 12,
                border: 'none',
                background: state.budgetCarryover ? 'var(--grn)' : 'var(--bdr)',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background .2s',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  left: state.budgetCarryover ? 22 : 2,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left .2s',
                }}
              />
            </button>
          </div>
        </div>
      )}

      {/* Category Budgets */}
      {tab === 'budget' &&
        (() => {
          const catBudgets = state.catBudgets || {};
          const allocated = allCats.reduce((s, c) => s + (parseFloat(catBudgets[c.id]) || 0), 0);
          const allocPct = varBudget > 0 ? Math.min(100, (allocated / varBudget) * 100) : 0;
          const remaining = varBudget - allocated;
          const overAlloc = allocated > varBudget && varBudget > 0;
          const suggestions = getSmartCatBudgets(state, varBudget);

          const setCat = (id, val) => {
            const v = parseFloat(val);
            const next = { ...catBudgets, [id]: isNaN(v) || v <= 0 ? undefined : v };
            Object.keys(next).forEach((k) => next[k] === undefined && delete next[k]);
            updateState({ catBudgets: next });
          };

          const applyAll = () => {
            const next = { ...catBudgets };
            Object.entries(suggestions).forEach(([id, s]) => {
              next[id] = s.amt;
            });
            updateState({ catBudgets: next });
            showToast('Suggested budgets applied!');
          };

          return (
            <div className="card cp" style={{ marginTop: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <div className="st" style={{ marginBottom: 0 }}>
                  📊 Category Budgets
                </div>
                {varBudget > 0 && (
                  <button
                    onClick={applyAll}
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: 8,
                      background: 'var(--ylw)',
                      color: '#000',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    ✨ Apply suggestions
                  </button>
                )}
              </div>
              <p style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 10 }}>
                Set a spending cap per category. Suggestions use your own spending once you have 2+
                months of history, otherwise your {fc(varBudget)} variable budget and household size.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
                <span style={{ fontSize: 12, fontWeight: 600, marginRight: 4 }}>Household size</span>
                {[1, 2, 3, 4, 5].map((n) => {
                  const on = (state.householdSize || 1) === n;
                  return (
                    <button
                      key={n}
                      onClick={() => updateState({ householdSize: n })}
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        minWidth: 30,
                        padding: '4px 8px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        border: `1.5px solid ${on ? 'var(--ylw)' : 'var(--bdr)'}`,
                        background: on ? 'rgba(255,217,61,.12)' : 'none',
                        color: on ? 'var(--ylw)' : 'var(--mut)',
                      }}
                    >
                      {n === 5 ? '5+' : n}
                    </button>
                  );
                })}
              </div>

              {varBudget > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: 5,
                    }}
                  >
                    <span style={{ fontSize: 11, color: 'var(--mut)' }}>Allocated</span>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: overAlloc
                          ? 'var(--red)'
                          : remaining === 0
                            ? 'var(--grn)'
                            : 'var(--txt)',
                      }}
                    >
                      {fc(allocated)}{' '}
                      <span style={{ color: 'var(--mut)', fontWeight: 400 }}>
                        of {fc(varBudget)}
                      </span>
                    </span>
                  </div>
                  <div className="pw">
                    <div
                      className="pb"
                      style={{
                        background: overAlloc ? 'var(--red)' : 'var(--grn)',
                        width: allocPct + '%',
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 3 }}>
                    {overAlloc
                      ? `⚠️ Over-allocated by ${fc(allocated - varBudget)}`
                      : remaining > 0
                        ? `${fc(remaining)} unallocated — available as a catch-all`
                        : '✓ Fully allocated'}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {allCats.map((cat) => {
                  const val = catBudgets[cat.id] || '';
                  const sug = suggestions[cat.id];
                  return (
                    <div
                      key={cat.id}
                      style={{
                        padding: '10px',
                        background: 'var(--surf2)',
                        borderRadius: 9,
                        border: '1px solid var(--bdr)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          marginBottom: sug ? 6 : 0,
                        }}
                      >
                        <span
                          style={{ fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0 }}
                        >
                          {cat.em}
                        </span>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: cat.col }}>
                          {cat.name}
                        </span>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            background: 'var(--bg)',
                            border: '1px solid var(--bdr)',
                            borderRadius: 7,
                            padding: '0 8px',
                          }}
                        >
                          <span style={{ fontSize: 12, color: 'var(--mut)' }}>$</span>
                          <input
                            type="number"
                            min="0"
                            placeholder="—"
                            value={val}
                            onChange={(e) => setCat(cat.id, e.target.value)}
                            style={{
                              width: 70,
                              background: 'none',
                              border: 'none',
                              fontSize: 13,
                              fontWeight: 700,
                              color: 'var(--txt)',
                              padding: '6px 0',
                              fontFamily: 'inherit',
                            }}
                            onFocus={(e) => (e.target.style.outline = 'none')}
                          />
                        </div>
                      </div>
                      {sug && (
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 34 }}
                        >
                          <span style={{ fontSize: 10, color: 'var(--mut)' }}>
                            Suggested: <strong>{fc(sug.amt)}</strong> · {sug.note}
                          </span>
                          <button
                            onClick={() => setCat(cat.id, sug.amt)}
                            style={{
                              fontSize: 10,
                              padding: '2px 8px',
                              borderRadius: 6,
                              border: '1px solid var(--bdr)',
                              background: 'var(--surf)',
                              color: 'var(--mut)',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

      {wizardOpen && (
        <SetupWizardModal
          mode="edit"
          state={state}
          updateState={updateState}
          showToast={showToast}
          onClose={() => setWizardOpen(false)}
        />
      )}

      {/* Bills */}
      {tab === 'bills' && (
        <div className="card cp">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div className="st" style={{ marginBottom: 0 }}>📋 Fixed Bills</div>
            <button
              onClick={() => setWizardOpen(true)}
              style={{
                padding: '6px 12px', borderRadius: 20,
                background: 'var(--ylw)', color: '#000',
                border: 'none', fontWeight: 800, fontSize: 11,
                cursor: 'pointer', fontFamily: 'inherit',
                letterSpacing: '.2px',
              }}
            >✨ Manage with AI</button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 16 }}>
            Add your recurring monthly bills — rent, utilities, subscriptions, etc.
          </p>
          <div className="ob-add-row">
            <input
              className="ob-inp"
              placeholder="Bill name"
              value={billName}
              onChange={(e) => setBillName(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <div className="ob-amt-wrap">
                <span>$</span>
                <input
                  type="number"
                  placeholder="Amount"
                  value={billAmt}
                  onChange={(e) => setBillAmt(e.target.value)}
                  min="0"
                />
              </div>
              <input
                className="ob-inp"
                style={{ width: 70 }}
                placeholder="Due date"
                type="number"
                min="1"
                max="31"
                value={billDue}
                onChange={(e) => setBillDue(e.target.value)}
              />
            </div>
            <div className="ob-cols">
              {COLORS.map((c) => (
                <button
                  key={c}
                  className={'ob-col' + (billCol === c ? ' on' : '')}
                  style={{ background: c }}
                  onClick={() => setBillCol(c)}
                />
              ))}
            </div>
            <button className="vad" onClick={addBill}>
              + Add
            </button>
          </div>
          {billErr && (
            <div className="lerr" style={{ marginTop: 8 }}>
              {billErr}
            </div>
          )}
          <div className="ob-list" style={{ marginTop: 12 }}>
            {!fixedBills.length && <div className="em">No bills added yet</div>}
            {fixedBills.map((b) => {
              const isEditing = editingBill?.id === b.id;
              if (isEditing)
                return (
                  <div
                    key={b.id}
                    className="ob-item"
                    style={{ flexWrap: 'wrap', gap: 6, alignItems: 'center' }}
                  >
                    <input
                      className="ob-inp"
                      value={editingBill.name}
                      onChange={(e) => setEditingBill((x) => ({ ...x, name: e.target.value }))}
                      style={{ flex: 1, minWidth: 100 }}
                    />
                    <div className="ob-amt-wrap">
                      <span>$</span>
                      <input
                        type="number"
                        min="0"
                        value={editingBill.amt}
                        onChange={(e) => setEditingBill((x) => ({ ...x, amt: e.target.value }))}
                        style={{ width: 70 }}
                      />
                    </div>
                    <input
                      className="ob-inp"
                      type="number"
                      min="1"
                      max="31"
                      placeholder="Due date"
                      value={editingBill.due}
                      onChange={(e) => setEditingBill((x) => ({ ...x, due: e.target.value }))}
                      style={{ width: 70 }}
                    />
                    <div style={{ display: 'flex', gap: 3 }}>
                      {COLORS.map((c) => (
                        <button
                          key={c}
                          className={'ob-col' + (editingBill.col === c ? ' on' : '')}
                          style={{ background: c }}
                          onClick={() => setEditingBill((x) => ({ ...x, col: c }))}
                        />
                      ))}
                    </div>
                    <button
                      className="vad"
                      style={{ padding: '4px 12px', fontSize: 12 }}
                      onClick={saveEditBill}
                    >
                      Save
                    </button>
                    <button className="del" onClick={() => setEditingBill(null)}>
                      ✕
                    </button>
                  </div>
                );
              return (
                <div key={b.id} className="ob-item">
                  <div
                    style={{
                      background: b.col,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong>{b.name}</strong> — ${b.amt} · Due {nextDueDate(b.due)}
                  </div>
                  <button
                    className="del"
                    style={{ fontSize: 13, marginRight: 4 }}
                    onClick={() => setEditingBill({ ...b })}
                  >
                    ✏️
                  </button>
                  <button className="del" onClick={() => removeBill(b.id)}>
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'bills' && (
        <div className="card cp" style={{ marginTop: 12 }}>
          <div className="st">🔄 Subscriptions</div>
          <p style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 14 }}>
            Track recurring subscriptions — Netflix, Spotify, gym memberships, etc.
          </p>
          <div className="ob-add-row">
            <input
              className="ob-inp"
              placeholder="Name (e.g. Netflix)"
              value={subName}
              onChange={(e) => setSubName(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <div className="ob-amt-wrap">
                <span>$</span>
                <input
                  type="number"
                  placeholder="Amount"
                  value={subAmt}
                  onChange={(e) => setSubAmt(e.target.value)}
                  min="0"
                />
              </div>
              <select
                value={subCycle}
                onChange={(e) => setSubCycle(e.target.value)}
                className="ob-sel"
                style={{ width: 'auto', padding: '6px 10px' }}
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
              <input
                className="ob-inp"
                style={{ width: 70 }}
                placeholder="Due date"
                type="number"
                min="1"
                max="31"
                value={subDue}
                onChange={(e) => setSubDue(e.target.value)}
              />
            </div>
            <div className="ob-cols">
              {SUB_COLORS.map((c) => (
                <button
                  key={c}
                  className={'ob-col' + (subCol === c ? ' on' : '')}
                  style={{ background: c }}
                  onClick={() => setSubCol(c)}
                />
              ))}
            </div>
            <button className="vad" onClick={addSub}>
              + Add
            </button>
          </div>
          {!subscriptions.length && (
            <div className="em" style={{ marginTop: 10 }}>
              No subscriptions tracked yet
            </div>
          )}
          {subscriptions.length > 0 && (
            <>
              <div className="ob-list" style={{ marginTop: 10 }}>
                {subscriptions.map((s) => {
                  const monthly = s.cycle === 'annual' ? s.amt / 12 : s.amt;
                  // Compute days until next charge from the due-day field
                  const todayNum = new Date().getDate();
                  const daysUntil = s.due
                    ? s.due >= todayNum
                      ? s.due - todayNum
                      : Math.round(
                          (new Date(new Date().getFullYear(), new Date().getMonth() + 1, s.due) -
                            new Date()) /
                            86400000
                        )
                    : null;
                  return (
                    <div key={s.id} className="ob-item">
                      <div
                        style={{
                          background: s.col,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <strong>{s.name}</strong>
                        <span style={{ color: 'var(--mut)', fontSize: 11 }}>
                          {' '}
                          — ${s.amt}/{s.cycle === 'annual' ? 'yr' : 'mo'}
                        </span>
                        {s.cycle === 'annual' && (
                          <span style={{ color: 'var(--mut)', fontSize: 10 }}>
                            {' '}
                            (${monthly.toFixed(2)}/mo)
                          </span>
                        )}
                        {daysUntil !== null && (
                          <span
                            style={{
                              fontSize: 10,
                              marginLeft: 6,
                              color: daysUntil <= 3 ? 'var(--ylw)' : 'var(--mut)',
                              fontWeight: daysUntil <= 3 ? 700 : 400,
                            }}
                          >
                            {daysUntil === 0
                              ? '· due today'
                              : daysUntil === 1
                              ? '· due tomorrow'
                              : `· due in ${daysUntil}d`}
                          </span>
                        )}
                      </div>
                      <button className="del" onClick={() => removeSub(s.id)}>
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  marginTop: 10,
                  padding: '8px 12px',
                  background: 'var(--surf2)',
                  borderRadius: 8,
                  border: '1px solid var(--bdr)',
                  fontSize: 11,
                  color: 'var(--mut)',
                }}
              >
                Total:{' '}
                <strong style={{ color: 'var(--txt)' }}>
                  $
                  {subscriptions
                    .reduce((s, x) => s + (x.cycle === 'annual' ? x.amt / 12 : x.amt), 0)
                    .toFixed(2)}
                  /mo
                </strong>
                {' · '}
                <strong style={{ color: 'var(--txt)' }}>
                  $
                  {subscriptions
                    .reduce((s, x) => s + (x.cycle === 'annual' ? x.amt : x.amt * 12), 0)
                    .toFixed(0)}
                  /yr
                </strong>
              </div>
            </>
          )}
        </div>
      )}

      {/* Appearance */}
      {tab === 'appear' && (
        <div>
          <div className="card cp" style={{ marginBottom: 12 }}>
            <div className="st">🎨 Theme</div>
            <p style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 14 }}>
              Choose a color theme for your dashboard.
            </p>
            <div className="theme-opts">
              {[
                { id: 'dark',     name: 'Dark',     bg: '#0f0f14', surf: '#1a1a24', accent: '#ffd93d', levelReq: 1 },
                { id: 'light',    name: 'Light',    bg: '#f0f0f5', surf: '#ffffff', accent: '#4d96ff', levelReq: 1 },
                { id: 'midnight', name: 'Midnight', bg: '#070710', surf: '#0d0d1e', accent: '#7C6FF7', levelReq: 10 },
                { id: 'forest',   name: 'Forest',   bg: '#0a1a0e', surf: '#112218', accent: '#a8d044', levelReq: 12 },
                { id: 'ocean',    name: 'Ocean',    bg: '#050e1a', surf: '#0a1828', accent: '#38beff', levelReq: 14 },
                { id: 'sunset',   name: 'Sunset',   bg: '#1a0d08', surf: '#2a1610', accent: '#f5a623', levelReq: 16 },
                { id: 'neon',     name: 'Neon',     bg: '#050508', surf: '#0c0c14', accent: '#ffe600', levelReq: 18 },
              ].map((t) => {
                const locked = currentLevel < t.levelReq;
                return (
                  <button
                    key={t.id}
                    className={'theme-opt' + (theme === t.id ? ' on' : '')}
                    onClick={() => { if (!locked) setTheme(t.id); }}
                    title={locked ? `Unlocks at Level ${t.levelReq}` : t.name}
                    style={{ opacity: locked ? 0.5 : 1, cursor: locked ? 'not-allowed' : 'pointer' }}
                  >
                    <div
                      className="theme-swatch"
                      style={{ background: `linear-gradient(135deg,${t.bg} 50%,${t.surf} 50%)`, borderColor: t.accent }}
                    />
                    <div className="theme-name">{t.name}</div>
                    {theme === t.id ? (
                      <div style={{ fontSize: 9, color: 'var(--ylw)', fontWeight: 700 }}>Active</div>
                    ) : locked ? (
                      <div style={{ fontSize: 9, color: 'var(--mut)', fontWeight: 700 }}>Lv {t.levelReq}</div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="card cp">
            <div className="st">📊 Dashboard Widgets</div>
            <p style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 14 }}>
              Show or hide sections of your dashboard.
            </p>
            {WIDGET_LIST.map((w) => (
              <div key={w.id} className="tog-row">
                <div className="tog-lbl">
                  {w.label}
                  <div className="tog-desc">{w.desc}</div>
                </div>
                <label className="tog">
                  <input
                    type="checkbox"
                    checked={dashWidgets[w.id] !== false}
                    onChange={(e) => setWidget(w.id, e.target.checked)}
                  />
                  <span className="tog-sw" />
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Account */}
      {tab === 'account' && (
        <>
        {/* Feature Preferences */}
        <div className="card cp" style={{ marginBottom: 12 }}>
          <div className="st" style={{ marginBottom: 4 }}>🎛️ Feature Preferences</div>
          <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 16 }}>
            Use only the parts you want. Changes take effect immediately — nothing is deleted.
          </div>

          {[
            {
              key: 'social',
              em: '🌐',
              title: 'Social Features',
              desc: 'Feed, leaderboard, clans, battles, DMs, boss events — the full community layer. Turn off to use Frujo as a private finance tracker only.',
            },
            {
              key: 'gamification',
              em: '⚡',
              title: 'XP & Level System',
              desc: 'Daily quests, streaks, level-up ceremonies, badges, and the XP engine. Turn off for a clean, no-game experience.',
            },
            {
              key: 'paydayAlerts',
              em: '💸',
              title: 'Payday Notifications',
              desc: "A morning notification on payday with how much is here, what's due before your next paycheck, and a tap to see the plan. Needs notifications allowed in your browser.",
            },
          ].map(({ key, em, title, desc }) => {
            const features = state.features || { social: true, gamification: true };
            const enabled = features[key] !== false;
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0', borderBottom: '1px solid var(--bdr)' }}>
                <span style={{ fontSize: 24, marginTop: 2 }}>{em}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 11, color: 'var(--mut)', lineHeight: 1.5 }}>{desc}</div>
                </div>
                <label className="tog" style={{ cursor: 'pointer', flexShrink: 0, marginTop: 4 }}>
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => updateState({ features: { ...(state.features || {}), [key]: e.target.checked } })}
                  />
                  <span className="tog-sw" />
                </label>
              </div>
            );
          })}
        </div>

        <div className="card cp">
          <div className="st">📤 Export Data</div>
          <p style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 12 }}>
            Download your data as a JSON backup or export transactions as a CSV spreadsheet.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
            <button
              className="btn"
              style={{
                background: 'var(--surf2)',
                color: 'var(--txt)',
                border: '1px solid var(--bdr)',
              }}
              onClick={exportData}
            >
              ⬇ Download Backup (JSON)
            </button>
            <button
              className="btn"
              style={{ background: 'var(--surf2)', color: 'var(--txt)', border: '1px solid var(--bdr)' }}
              onClick={importBackup}
            >
              ⬆ Restore from Backup
            </button>
            <button
              className="btn"
              style={{
                background: 'var(--surf2)',
                color: 'var(--txt)',
                border: '1px solid var(--bdr)',
              }}
              onClick={exportCSV}
            >
              ⬇ Export Transactions (CSV)
            </button>
          </div>
          <div className="st">👤 Change Password</div>
          <div className="lf">
            <label>Current Password</label>
            <input
              type="password"
              className="lf input"
              value={curPw}
              onChange={(e) => setCurPw(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: '1px solid var(--bdr)',
                borderRadius: 8,
                background: 'var(--surf2)',
                color: 'var(--txt)',
                fontSize: 14,
              }}
            />
          </div>
          <div className="lf">
            <label>New Password</label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: '1px solid var(--bdr)',
                borderRadius: 8,
                background: 'var(--surf2)',
                color: 'var(--txt)',
                fontSize: 14,
              }}
            />
          </div>
          <div className="lf">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confPw}
              onChange={(e) => setConfPw(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: '1px solid var(--bdr)',
                borderRadius: 8,
                background: 'var(--surf2)',
                color: 'var(--txt)',
                fontSize: 14,
              }}
            />
          </div>
          {pwErr && <div className="lerr">{pwErr}</div>}
          <button className="btn" onClick={handlePasswordChange} style={{ marginTop: 8 }}>
            Update Password
          </button>
        </div>
        <InstallCard />
        <DeleteAccountCard onExport={exportData} />
        </>
      )}

      {/* Users (admin only) */}
      {tab === 'users' && isAdmin && (
        <div>
          <div className="card cp" style={{ marginBottom: 12 }}>
            <div className="st">📨 Invite Someone</div>
            <p style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 12 }}>
              Generate a 24-hour invite link. Send it to anyone you want to give access to.
            </p>
            <button className="btn" onClick={handleCreateInvite} disabled={loading}>
              {loading ? 'Generating...' : '+ Generate Invite Link'}
            </button>
            {invites.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--mut)',
                    textTransform: 'uppercase',
                    letterSpacing: '.4px',
                    marginBottom: 8,
                  }}
                >
                  Recent Invites
                </div>
                {invites.map((inv) => {
                  const expired = new Date(inv.expires_at) < new Date();
                  const link = inv.link || window.location.origin + '/register?token=' + inv.token;
                  return (
                    <div
                      key={inv.id}
                      style={{
                        background: 'var(--surf2)',
                        borderRadius: 9,
                        padding: '10px 12px',
                        marginBottom: 8,
                        border: '1px solid var(--bdr)',
                      }}
                    >
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}
                      >
                        <div
                          style={{
                            flex: 1,
                            fontSize: 11,
                            color: inv.used_at
                              ? 'var(--grn)'
                              : expired
                                ? 'var(--red)'
                                : 'var(--ylw)',
                            fontWeight: 700,
                          }}
                        >
                          {inv.used_at
                            ? '✓ Used by ' + (inv.used_by || 'someone')
                            : expired
                              ? '✕ Expired'
                              : '⏳ Active — expires ' +
                                new Date(inv.expires_at).toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                        </div>
                        {!inv.used_at && !expired && (
                          <button className="del" onClick={() => handleDeleteInvite(inv.id)}>
                            ✕
                          </button>
                        )}
                      </div>
                      {!inv.used_at && !expired && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <div
                            style={{
                              flex: 1,
                              fontSize: 10,
                              color: 'var(--mut)',
                              background: 'var(--bg)',
                              borderRadius: 6,
                              padding: '5px 8px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {link}
                          </div>
                          <button
                            className="vad"
                            style={{ fontSize: 11, padding: '4px 10px' }}
                            onClick={() => copyLink(link, inv.id)}
                          >
                            {copied === inv.id ? '✓ Copied!' : 'Copy'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="card cp">
            <div className="st">👥 All Users</div>
            {users.map((u) => (
              <div
                key={u.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 0',
                  borderBottom: '1px solid var(--bdr)',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {u.username}{' '}
                    {u.is_admin && (
                      <span
                        style={{
                          fontSize: 9,
                          background: 'var(--ylw)',
                          color: '#000',
                          borderRadius: 4,
                          padding: '1px 5px',
                          fontWeight: 700,
                          marginLeft: 4,
                        }}
                      >
                        ADMIN
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 1 }}>
                    Joined {new Date(u.created_at).toLocaleDateString()} ·{' '}
                    {u.onboarded ? 'Set up ✓' : 'Not set up yet'}
                    {u.last_active && (
                      <> · Last active {new Date(u.last_active).toLocaleDateString()}</>
                    )}
                  </div>
                  {(u.level || u.streak !== null) && (
                    <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 1, display: 'flex', gap: 8 }}>
                      {u.level && <span>Lv{u.level}</span>}
                      {u.xp > 0 && <span>{u.xp.toLocaleString()} XP</span>}
                      <span style={{ color: u.streak > 0 ? 'var(--grn)' : 'var(--mut)' }}>
                        🔥 {u.streak ?? 0}mo streak
                      </span>
                      <span style={{ color: 'var(--blu)' }}>{Math.round(u.rank_score ?? 0)} score</span>
                    </div>
                  )}
                </div>
                {!u.is_admin && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="del"
                        style={{ color: 'var(--blu)', borderColor: 'var(--blu)' }}
                        onClick={() => handleResetPassword(u.id)}
                      >
                        Reset PW
                      </button>
                      <button
                        className="del"
                        style={{ color: 'var(--red)' }}
                        onClick={() => handleDeleteUser(u.id, u.username)}
                      >
                        Remove
                      </button>
                    </div>
                    {resetLinks[u.id] && (
                      <div style={{ fontSize: 10, color: 'var(--mut)', maxWidth: 220, wordBreak: 'break-all', textAlign: 'right' }}>
                        🔑 <span style={{ color: 'var(--blu)' }}>{resetLinks[u.id]}</span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(resetLinks[u.id]); showToast('Copied!'); }}
                          style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 4, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}
                        >
                          Copy
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feedback / Bug Report */}
      {tab === 'feedback' && <FeedbackPanel username={state._username} />}

      {/* Reports (admin only) */}
      {tab === 'reports' && isAdmin && <ReportsPanel />}

    </div>
  );
}

// ── Feedback Panel ────────────────────────────────────────────────────────────
function FeedbackPanel() {
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const TYPES = [
    { id: 'bug',        label: '🐛 Bug Report',   desc: 'Something is broken or not working right' },
    { id: 'suggestion', label: '💡 Suggestion',   desc: 'Feature idea or improvement' },
    { id: 'question',   label: '❓ Question',     desc: 'Need help understanding something' },
    { id: 'other',      label: '📝 Other',        desc: 'Anything else' },
  ];

  const submit = async () => {
    if (!message.trim() || message.length < 5) return;
    setSending(true);
    const meta = { url: window.location.href, ua: navigator.userAgent.slice(0, 120) };
    const { submitFeedback } = await import('../api');
    const r = await submitFeedback(type, message.trim(), meta);
    setSending(false);
    if (r.ok) { setSent(true); setMessage(''); }
  };

  if (sent) return (
    <div className="card cp" style={{ textAlign: 'center', padding: 32 }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
      <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Thanks for the feedback!</div>
      <div style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 20 }}>We'll look into it soon.</div>
      <button onClick={() => setSent(false)} style={{ padding: '8px 20px', borderRadius: 20, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer' }}>Send Another</button>
    </div>
  );

  return (
    <div className="card cp">
      <div className="st" style={{ marginBottom: 4 }}>🐛 Report a Bug or Share Feedback</div>
      <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 16 }}>All reports go directly to the dev. We read every one.</div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {TYPES.map(t => (
          <button key={t.id} onClick={() => setType(t.id)} style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${type === t.id ? 'var(--ylw)' : 'var(--bdr)'}`, background: type === t.id ? 'rgba(255,217,61,.12)' : 'none', color: type === t.id ? 'var(--ylw)' : 'var(--mut)' }}>
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 8 }}>
        {TYPES.find(t => t.id === type)?.desc}
      </div>

      <textarea
        className="ob-inp"
        placeholder={type === 'bug' ? 'What happened? What did you expect? Steps to reproduce…' : 'Describe your idea or question…'}
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={5}
        maxLength={2000}
        style={{ width: '100%', resize: 'vertical', marginBottom: 8, fontSize: 13 }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 10, color: 'var(--mut)' }}>{message.length}/2000 · browser info sent automatically</div>
        <button onClick={submit} disabled={sending || message.trim().length < 5} style={{ padding: '9px 22px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 13, opacity: message.trim().length < 5 ? 0.5 : 1 }}>
          {sending ? 'Sending…' : 'Send Report'}
        </button>
      </div>
    </div>
  );
}

// ── Reports Panel (admin) ─────────────────────────────────────────────────────
function ReportsPanel() {
  const [postReports, setPostReports] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [view, setView] = useState('feedback');

  const load = async () => {
    // eslint-disable-next-line no-unused-vars
    const { getPostReports, getAdminFeedback, resolvePostReport, resolveFeedback } = await import('../api');
    const [pr, fb] = await Promise.all([getPostReports(), getAdminFeedback()]);
    if (pr.reports) setPostReports(pr.reports);
    if (fb.feedback) setFeedback(fb.feedback);
  };

  React.useEffect(() => { load(); }, []);

  const resolve = async (type, id) => {
    const { resolvePostReport, resolveFeedback } = await import('../api');
    if (type === 'post') await resolvePostReport(id);
    else await resolveFeedback(id);
    load();
  };

  const TYPE_COL = { bug: 'var(--red)', suggestion: 'var(--ylw)', question: 'var(--blu)', other: 'var(--mut)' };
  const TABS = [{ id: 'feedback', label: `💡 Feedback (${feedback.filter(f => !f.resolved).length})` }, { id: 'posts', label: `🚩 Post Reports (${postReports.length})` }];

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setView(t.id)} style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${view === t.id ? 'var(--ylw)' : 'var(--bdr)'}`, background: view === t.id ? 'rgba(255,217,61,.12)' : 'none', color: view === t.id ? 'var(--ylw)' : 'var(--mut)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {view === 'feedback' && (
        feedback.length === 0 ? <div className="em">No feedback yet.</div> : feedback.map(f => (
          <div key={f.id} className="card cp" style={{ marginBottom: 10, opacity: f.resolved ? 0.5 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: TYPE_COL[f.type] || 'var(--mut)', padding: '2px 8px', border: `1px solid ${TYPE_COL[f.type] || 'var(--bdr)'}`, borderRadius: 20 }}>{f.type}</span>
              <span style={{ fontSize: 11, color: 'var(--mut)' }}>from <strong>{f.username}</strong></span>
              <span style={{ fontSize: 10, color: 'var(--mut)', marginLeft: 'auto' }}>{new Date(f.created_at).toLocaleDateString()}</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--txt)', marginBottom: 10, whiteSpace: 'pre-wrap' }}>{f.message}</div>
            {!f.resolved && (
              <button onClick={() => resolve('feedback', f.id)} style={{ padding: '5px 14px', borderRadius: 20, background: 'var(--grn)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 11 }}>
                ✓ Mark Resolved
              </button>
            )}
          </div>
        ))
      )}

      {view === 'posts' && (
        postReports.length === 0 ? <div className="em">No post reports.</div> : postReports.map(r => (
          <div key={r.id} className="card cp" style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)', padding: '2px 8px', border: '1px solid var(--red)', borderRadius: 20 }}>{r.reason}</span>
              <span style={{ fontSize: 11, color: 'var(--mut)' }}>reported by <strong>{r.reporter}</strong></span>
              <span style={{ fontSize: 10, color: 'var(--mut)', marginLeft: 'auto' }}>{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 4 }}>Post by: {r.post_author}</div>
            <div style={{ fontSize: 13, background: 'var(--surf2)', padding: '8px 12px', borderRadius: 8, marginBottom: 10, color: 'var(--txt)' }}>{r.content}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => resolve('post', r.id)} style={{ padding: '5px 14px', borderRadius: 20, background: 'var(--grn)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 11 }}>
                ✓ Dismiss Report
              </button>
              <button onClick={async () => { const { deletePost } = await import('../api'); await deletePost(r.post_id); resolve('post', r.id); }} style={{ padding: '5px 14px', borderRadius: 20, background: 'var(--red)', color: '#fff', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 11 }}>
                🗑 Delete Post
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
